import React from 'react';
import { Box, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import TimerIcon from '@mui/icons-material/Timer';
import { useNavigate } from 'react-router-dom';

const Header = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  position: 'sticky',
  top: 0,
  backgroundColor: '#fff',
  borderBottom: '1px solid #E0E0E0',
  zIndex: 1000,
  boxShadow: '0px 2px 4px rgba(0, 0, 0, 0.05)',
  padding: '12px 20px',
  [theme.breakpoints.down('sm')]: {
    padding: '10px 12px',
  },
}));

const TimerContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#333',
  fontWeight: 'medium',
  '& .MuiSvgIcon-root': {
    color: '#996CFE',
    marginRight: '8px',
  },
}));

const TimerNumber = styled('span')(({ theme }) => ({
  fontWeight: 700,
  fontSize: '1.15em',
  color: '#2D3748',
}));

/** One shared box model for Review / Pause / Submit so they render the same size (2px border on all). */
const ieltsHeaderActionButtonLayout = (theme) => ({
  boxSizing: 'border-box',
  height: 48,
  minHeight: 48,
  maxHeight: 48,
  px: '22px',
  fontSize: '1rem',
  fontWeight: 600,
  lineHeight: 1,
  textTransform: 'none',
  borderRadius: '10px',
  borderStyle: 'solid',
  borderWidth: 1,
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  '& .MuiButton-startIcon': {
    marginRight: '8px',
    marginLeft: 0,
  },
  '& .MuiButton-endIcon': {
    marginLeft: '8px',
    marginRight: 0,
  },
  [theme.breakpoints.down('sm')]: {
    height: 46,
    minHeight: 46,
    maxHeight: 46,
    px: '18px',
    fontSize: '0.9375rem',
  },
});

const IELTSHeader = ({
  timerText,
  middleContent = null,
  showReview = false,
  showSaveExit = false,
  showSubmit = true,
  isSubmitting = false,
  apiResult = null,
  groupId = null,
  onReview,
  onSaveExit,
  onSubmit,
  onSubmitNavigate,
  submitButtonText,
  submitButtonDisabled = false,
  isSimulationMode = false,
  /** Full test only: pause / leave without API; parent handles navigation */
  onFullTestPause,
}) => {
  const navigate = useNavigate();
  const isActiveSimulation = isSimulationMode && !apiResult;
  const shouldShowSubmitButton = showSubmit && !isActiveSimulation;

  const handleSubmitClick = () => {
    if (apiResult && !groupId && onSubmitNavigate) {
      navigate(onSubmitNavigate);
    } else if (onSubmit) {
      onSubmit();
    }
  };

  const getSubmitButtonText = () => {
    if (submitButtonText) return submitButtonText;
    if (apiResult && !groupId) return 'Results';
    if (isSubmitting) return 'Submitting...';
    return 'Submit';
  };

  const renderTimerText = (text) => {
    if (text === 'Unlimited') return text;
    
    // Match numbers (including negative) in the text
    const parts = text.split(/(\d+)/);
    return parts.map((part, index) => {
      if (/^\d+$/.test(part)) {
        return <TimerNumber key={index}>{part}</TimerNumber>;
      }
      return part;
    });
  };

  return (
    <Header>
      {/* Left: Logo */}
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <img src="/newLogo.svg" alt="IELTS Logo" height="32" />
      </Box>

      {/* Center: Timer and optional middle content */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        {timerText && (
          <TimerContainer>
            <TimerIcon />
            <Typography variant="body1" component="span">
              {renderTimerText(timerText)}
            </Typography>
          </TimerContainer>
        )}
        {middleContent}
      </Box>

      {/* Right: Action buttons */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {showReview && (
          <Button
            variant="outlined"
            startIcon={<Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>📋</Box>}
            onClick={onReview}
            sx={(theme) => ({
              ...ieltsHeaderActionButtonLayout(theme),
              color: '#996CFE',
              borderColor: '#996CFE',
              backgroundColor: 'transparent',
              '&:hover': {
                borderColor: '#7C4DFF',
                backgroundColor: 'rgba(153, 108, 254, 0.08)',
              },
            })}
          >
            Review
          </Button>
        )}

        {/* Exit button */}
        {/* {showSaveExit && !apiResult && (
          <Button
            onClick={onSaveExit}
            sx={{
              backgroundColor: '#FFA726',
              color: 'white',
              borderRadius: '8px',
              padding: '6px 16px',
              fontSize: '14px',
              fontWeight: 600,
              textTransform: 'none',
              '&:hover': {
                backgroundColor: '#FF9800',
              },
            }}
            startIcon={<Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>🚪</Box>}
          >
            Exit
          </Button>
        )} */}

        {typeof onFullTestPause === 'function' && !apiResult && (
          <Button
            variant="outlined"
            onClick={onFullTestPause}
            disabled={isSubmitting}
            sx={(theme) => ({
              ...ieltsHeaderActionButtonLayout(theme),
              color: '#996CFE',
              borderColor: '#996CFE',
              backgroundColor: 'transparent',
              '&:hover': {
                borderColor: '#7C4DFF',
                backgroundColor: 'rgba(153, 108, 254, 0.06)',
              },
              '&.Mui-disabled': {
                borderColor: '#E0E0E0',
                color: '#BDBDBD',
              },
            })}
          >
            Pause
          </Button>
        )}

        {/* Submit/Results button */}
        {shouldShowSubmitButton && (
          <Button
            variant="contained"
            onClick={handleSubmitClick}
            disabled={submitButtonDisabled || isSubmitting}
            endIcon={<Box component="span" sx={{ display: 'flex', alignItems: 'center' }}>➔</Box>}
            sx={(theme) => ({
              ...ieltsHeaderActionButtonLayout(theme),
              backgroundColor: '#37965E',
              color: '#fff',
              borderColor: '#37965E',
              boxShadow: 'none',
              '&:hover': {
                backgroundColor: '#2A7349',
                borderColor: '#2A7349',
                boxShadow: 'none',
              },
              '&.Mui-disabled': {
                backgroundColor: '#E0E0E0',
                borderColor: '#E0E0E0',
                color: '#9E9E9E',
              },
            })}
          >
            {getSubmitButtonText()}
          </Button>
        )}
      </Box>
    </Header>
  );
};

export default IELTSHeader;
